import { goalTarget, project, rmdStartAge, UNIFORM_TABLE } from "@/lib/finance";

const THIS_YEAR = new Date().getFullYear();
const SS_WAGE_CAP = 184500;
const BEND = [1286, 7749];

const YR = 365.2425 * 864e5;
const parseD = (s) => { if (!s) return null; const d = new Date(`${s}T00:00:00`); return isNaN(d) ? null : d; };
export const birthDateOf = (p) => parseD(p.birth_date);
export const scdOf = (p) => parseD(p.service_start_date);
export const exactAge = (p, at = new Date()) => { const b = birthDateOf(p); return b ? (at - b) / YR : Number(p.age); };
export const serviceAt = (p, at = new Date()) => { const s = scdOf(p); return s ? Math.max(0, (at - s) / YR) : Number(p.service_years) || 0; };
export const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + Math.round(m)); return x; };
export const dateAtAge = (p, age) => { const b = birthDateOf(p); return b ? addMonths(b, age * 12) : null; };
export const fmtDate = (d) => d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
export const ageYM = (a) => { const y = Math.floor(a + 1e-6), m = Math.floor((a - y) * 12 + 1e-6); return `${y} y ${m} m`; };
export function syncDates(q) {
  const b = birthDateOf(q), s = scdOf(q);
  return { ...q, ...(b ? { age: Math.floor(exactAge(q)), birth_year: b.getFullYear() } : {}), ...(s ? { service_years: Math.round(serviceAt(q) * 10) / 10 } : {}) };
}

export const birthYearOf = (p) => birthDateOf(p)?.getFullYear() || Number(p.birth_year) || THIS_YEAR - Number(p.age);

export function mra(by) {
  if (by < 1948) return 55;
  if (by <= 1952) return 55 + ((by - 1947) * 2) / 12;
  if (by <= 1964) return 56;
  if (by <= 1969) return 56 + ((by - 1964) * 2) / 12;
  return 57;
}

export function fra(by) {
  if (by <= 1954) return 66;
  if (by <= 1959) return 66 + ((by - 1954) * 2) / 12;
  return 67;
}

export const ageLabel = (a) => {
  const y = Math.floor(a), m = Math.round((a - y) * 12);
  return m ? `${y} and ${m} mo` : `${y}`;
};

export function fersCola(infl) {
  if (infl <= 2) return infl;
  if (infl <= 3) return 2;
  return infl - 1;
}

export function high3(p) {
  if (Number(p.high3_override) > 0) return Number(p.high3_override);
  const n = Math.max(0, p.retire_age - exactAge(p));
  const g = ((Number(p.salary_growth) || 0) + (Number(p.step_increase_pct) || 0)) / 100;
  const s = (k) => (Number(p.salary) || 0) * Math.pow(1 + g, Math.max(0, k));
  return (s(n - 1) + s(n - 2) + s(n - 3)) / 3;
}

export function socialSecurity(p, claimAge = p.ss_claim_age) {
  const by = birthYearOf(p);
  const aime = Math.min(Number(p.salary) || 0, SS_WAGE_CAP) / 12;
  const formula = 0.9 * Math.min(aime, BEND[0]) + 0.32 * Math.max(0, Math.min(aime, BEND[1]) - BEND[0]) + 0.15 * Math.max(0, aime - BEND[1]);
  const pia = Number(p.ss_pia_override) > 0 ? Number(p.ss_pia_override) : formula;
  const f = fra(by);
  const adj = (age) => {
    const months = Math.round((age - f) * 12);
    if (months < 0) { const m = -months; return 1 - (Math.min(m, 36) * 5) / 900 - (Math.max(0, m - 36) * 5) / 1200; }
    return 1 + (Math.min(months, (70 - f) * 12) * (8 / 12)) / 100;
  };
  const infl = (Number(p.inflation) || 0) / 100;
  const grow = (age) => Math.pow(1 + infl, Math.max(0, age - p.age));
  return {
    pia, fra: f, estimated: !(Number(p.ss_pia_override) > 0), claimAge,
    monthlyToday: pia * adj(claimAge),
    annualAtClaim: pia * 12 * adj(claimAge) * grow(claimAge),
    at62Annual: pia * 12 * adj(62) * grow(62),
  };
}

export const SURVIVOR_COST = { none: 0, partial: 0.05, full: 0.1 };
export const survivorShare = (system, sb) => ({ none: 0, partial: system === "FERS" ? 0.25 : 0.275, full: system === "FERS" ? 0.5 : 0.55 })[sb];
export const fersImmediateUnreduced = (A, S, m) => (A >= 62 && S >= 5) || (A >= 60 && S >= 20) || (A >= m && S >= 30);
export const deferredStartAge = (A, S, m) => (S >= 30 ? Math.max(A, m) : S >= 20 ? Math.max(A, 60) : Math.max(A, 62));

export function fersEarlyOutRule(p, A, S, m, eo) {
  if (eo === "vera" && ((A >= 50 && S >= 20) || S >= 25)) {
    return { type: "VERA: immediate annuity with no age penalty", earlyOut: "vera", supp: true, suppStart: Math.max(A, m), notes: [`Under VERA the FERS Supplement starts at your MRA (${ageLabel(m)}).`] };
  }
  if (eo === "vsip" && S >= 5) {
    const startAge = deferredStartAge(A, S, m);
    return { startAge, type: `VSIP + deferred annuity starting at ${ageLabel(startAge)}`, earlyOut: "vsip",
      notes: [`VSIP lump sum of about $${Math.round(Number(p.early_out.vsip_amount) || 25000).toLocaleString()} at separation (taxable). FEHB ends at separation under a deferred annuity.`] };
  }
  return null;
}

export function fersRule(p, A, S, m) {
  const eo = p.early_out && Number(p.early_out.age) === A ? p.early_out.type : null;
  const early = !fersImmediateUnreduced(A, S, m) && fersEarlyOutRule(p, A, S, m, eo);
  if (early) return early;
  if (A >= 62 && S >= 5) return { type: "Immediate unreduced annuity (age 62 with 5+ years)" };
  if (A >= 60 && S >= 20) return { type: "Immediate unreduced annuity (age 60 with 20+ years)", supp: true };
  if (A >= m && S >= 30) return { type: `Immediate unreduced annuity (MRA ${ageLabel(m)} with 30+ years)`, supp: true };
  if (A >= m && S >= 10) {
    return { type: "MRA+10 annuity, reduced 5% per year under 62", reduction: Math.min(1, 0.05 * (62 - A)), earlyOut: eo === "mra10" ? "mra10" : null,
      notes: ["You can postpone the MRA+10 annuity start to shrink or remove the reduction."] };
  }
  if (S >= 5) return { type: "Deferred annuity starting at age 62", startAge: 62 };
  return { type: "Not eligible: FERS needs at least 5 years of service", ineligible: true };
}

export const fersSupplement = (p, S) => (socialSecurity(p, 62).at62Annual * Math.min(S, 40)) / 40;

function fersPension(p, out, surv) {
  const A = out.startAge, S = out.service;
  const { supp, ineligible, notes = [], ...rule } = fersRule(p, A, S, out.mra);
  Object.assign(out, rule);
  out.notes.push(...notes);
  if (ineligible) return out;
  out.eligible = true;
  out.multiplier = A >= 62 && S >= 20 ? 1.1 : 1.0;
  out.base = (out.high3 * S * out.multiplier) / 100;
  out.annual = out.base * (1 - out.reduction) * (1 - surv);
  out.colaPct = fersCola(out.colaPct);
  out.colaFromAge = Math.max(62, out.startAge);
  if (supp) {
    out.supplement = fersSupplement(p, S);
    out.notes.push("The FERS Supplement stops at 62 and is subject to an earnings test if you work after retiring.");
  }
  return out;
}

export const csrsPercent = (S) => Math.min(80, 1.5 * Math.min(S, 5) + 1.75 * Math.max(0, Math.min(S, 10) - 5) + 2 * Math.max(0, S - 10));

function csrsPension(p, out, surv) {
  const A = out.startAge, S = out.service;
  if (!((A >= 62 && S >= 5) || (A >= 60 && S >= 20) || (A >= 55 && S >= 30))) return Object.assign(out, { type: "Not yet eligible for an immediate CSRS annuity" });
  const pct = csrsPercent(S);
  out.base = (out.high3 * pct) / 100;
  return Object.assign(out, { eligible: true, type: "CSRS immediate annuity", multiplier: pct / Math.max(S, 1), annual: out.base * (1 - surv) });
}

function brsPension(p, out, surv) {
  const S = out.service;
  if (S < 20) return Object.assign(out, { type: "Military pension requires 20+ years of service (TSP only under BRS)" });
  out.base = out.high3 * S * 0.02;
  out.notes.push("Use your High-3 basic pay in the override field for an accurate number.");
  return Object.assign(out, { eligible: true, type: "Military BRS retired pay (2% x years x High-3 base pay)", multiplier: 2, annual: out.base * (1 - surv) });
}

const PENSION_BY_SYSTEM = { FERS: fersPension, CSRS: csrsPension, BRS: brsPension };

export function pension(p) {
  const A = Number(p.retire_age);
  const sb = p.survivor_benefit || "none";
  const infl = Number(p.inflation) || 0;
  const out = { system: p.retirement_system, service: serviceAt(p) + Math.max(0, A - exactAge(p)), high3: high3(p), mra: mra(birthYearOf(p)), startAge: A, reduction: 0, multiplier: 0, annual: 0, base: 0,
    survivorPct: survivorShare(p.retirement_system, sb), supplement: 0, suppStart: A, colaPct: infl, colaFromAge: A, type: "", eligible: false, notes: [], earlyOut: null };
  const calc = PENSION_BY_SYSTEM[p.retirement_system];
  if (!calc) return Object.assign(out, { type: "No pension for civilian / private-sector plans" });
  return calc(p, out, SURVIVOR_COST[sb]);
}

export function guaranteedAtRetirement(p) {
  const pen = pension(p);
  const ss = socialSecurity(p);
  const A = p.retire_age;
  const pensionNow = pen.eligible && pen.startAge <= A ? pen.annual : 0;
  const ssNow = ss.claimAge <= A ? ss.annualAtClaim : 0;
  const bridge = ssNow || pen.suppStart > A ? 0 : pen.supplement;
  return { annual: pensionNow + bridge + ssNow, pension: pensionNow, supplement: bridge, ss: ssNow, pen, ssInfo: ss };
}

export function effectiveGoal(p) {
  const base = goalTarget(p);
  if (!p.include_pension) return { target: base, base, credit: 0, guaranteed: null };
  const g = guaranteedAtRetirement(p);
  const credit = g.annual / ((Number(p.withdrawal_rate) || 4) / 100);
  return { target: Math.max(0, base - credit), base, credit, guaranteed: g };
}

export function incomePlan(p, balances = {}) {
  const total = (balances.tsp || 0) + (balances.trad_ira || 0) + (balances.roth_ira || 0) + (balances.other || 0);
  const final = project(p, total).final.expected;
  const rs = (Number(p.roth_split) || 0) / 100;
  const tradFrac = total > 0 ? ((balances.tsp || 0) * (1 - rs) + (balances.trad_ira || 0)) / total : 1 - rs;
  let pre = final * tradFrac, post = final * (1 - tradFrac);
  const r = (Number(p.retire_return) || 0) / 100, infl = (Number(p.inflation) || 0) / 100, tax = (Number(p.tax_rate) || 0) / 100;
  const pen = pension(p), ss = socialSecurity(p), rmdAge = rmdStartAge(birthYearOf(p));
  const w0 = final * ((Number(p.withdrawal_rate) || 4) / 100);
  const rows = [];
  let penCur = 0, depletedAt = null;
  for (let age = p.retire_age, i = 0; age <= p.life_expectancy; age++, i++) {
    if (pen.eligible && age >= pen.startAge) penCur = penCur ? (age > pen.colaFromAge ? penCur * (1 + pen.colaPct / 100) : penCur) : pen.annual;
    const supp = pen.supplement && age < 62 && age >= pen.suppStart ? pen.supplement : 0;
    const ssAmt = age >= ss.claimAge ? ss.annualAtClaim * Math.pow(1 + infl, age - ss.claimAge) : 0;
    const want = w0 * Math.pow(1 + infl, i);
    const rmd = age >= rmdAge && pre > 0 ? pre / (UNIFORM_TABLE[age] || 4.6) : 0;
    const fromPre = Math.min(pre, Math.max(want, rmd));
    const fromPost = Math.min(post, Math.max(0, want - fromPre));
    const taxes = (fromPre + penCur + supp + 0.85 * ssAmt) * tax;
    const gross = penCur + supp + ssAmt + fromPre + fromPost;
    pre = (pre - fromPre) * (1 + r);
    post = (post - fromPost) * (1 + r);
    if (depletedAt == null && pre + post < 1 && want > 0) depletedAt = age;
    rows.push({ age, year: THIS_YEAR + (age - p.age), pension: penCur, supplement: supp, ss: ssAmt, fromPre, fromPost, withdrawal: fromPre + fromPost, rmd, gross, taxes, net: gross - taxes, monthly: (gross - taxes) / 12, balance: pre + post });
  }
  return { rows, startBalance: final, depletedAt, rmdAge, pen, ss };
}
